import React, { useState } from "react";
import { Card } from "../Card/Card";
import { BookingSearchLoading } from "./BookingSearchLoading";

import { RootDataType } from "../Helpers/interfaces";
import { selectDoctorAndLocation } from "../Helpers/treatmentDoctorHelpers";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../Helpers/formTypes";
import {
  findTreatmentById,
  selectContainer,
} from "../Helpers/treatmentsHelpers";
import { isLocationSelected } from "../Helpers/locationHelpers";
import { locationInitialState } from "../Helpers/initialState/locationInitialState";

import { BookingSearchResponse } from "../../../../Api/BookingSearch/Model/BookingSearchResponse.csharp";

import { StepName } from "../Hooks/form";
import { useBookingSearch } from "../Hooks/useBookingSearch";
import { useBookingContext } from "../../BookingButton/BookingContext/BookingContext";
import { LoadingAnimation } from "../../LoadingAnimation/LoadingAnimation";

interface BookingSearchResultsProps {
  searchValue: string;
  setSearchValue: (searchValue: string) => void;
  rootData: RootDataType;
  dispatch: FormDispatch;
  setStepByName: (stepName: StepName) => void;
  state: FormStateType;
}

type SectionConfig<T> = {
  title: string;
  items: T[];
  totalCount: number;
  sectionKey: "doctors" | "services" | "specializations";
  setShowAll: React.Dispatch<React.SetStateAction<boolean>>;
  getKey: (item: T) => string;
  onClick: (item: T) => void;
  renderCard: (item: T) => React.ReactNode;
};

const MAX_ITEMS = 3;

export const BookingSearchResults: React.FC<BookingSearchResultsProps> = ({
  searchValue,
  setSearchValue,
  rootData,
  dispatch,
  setStepByName,
  state,
}) => {
  const {
    results,
    setResults,
    isLoading,
    error,
    isTyping,
    showAllDoctors,
    setShowAllDoctors,
    showAllServices,
    setShowAllServices,
    showAllSpecializations,
    setShowAllSpecializations,
    performSearch,
  } = useBookingSearch(state.location, searchValue);

  const [loadingSections, setLoadingSections] = useState<{
    doctors: boolean;
    services: boolean;
    specializations: boolean;
  }>({
    doctors: false,
    services: false,
    specializations: false,
  });

  const setSectionLoading = (
    section: "doctors" | "services" | "specializations",
    value: boolean,
  ) => {
    setLoadingSections((prev) => ({
      ...prev,
      [section]: value,
    }));
  };

  const { bookingSearchSetup } = useBookingContext();

  const doctorResults = results.doctors?.items || [];
  const doctorTotal = results.doctors?.totalCount || 0;

  const serviceResults = results.services?.items || [];
  const serviceTotal = results.services?.totalCount || 0;

  const specializationResults = results.specializations?.items || [];
  const specializationTotal = results.specializations?.totalCount || 0;

  const displayDoctors = showAllDoctors
    ? doctorResults
    : doctorResults.slice(0, MAX_ITEMS);

  const displayServices = showAllServices
    ? serviceResults
    : serviceResults.slice(0, MAX_ITEMS);

  const displaySpecializations = showAllSpecializations
    ? specializationResults
    : specializationResults.slice(0, MAX_ITEMS);

  const totalItemsLength =
    displayDoctors.length +
    displayServices.length +
    displaySpecializations.length;

  const renderSection = <T,>({
    title,
    items,
    totalCount,
    sectionKey,
    setShowAll,
    getKey,
    onClick,
    renderCard,
  }: SectionConfig<T>) => {
    if (items.length === 0) return null;

    return (
      <div className="SearchSection">
        <h3 className="SearchSection__title">{title}</h3>
        {items.map((item) => (
          <button
            key={getKey(item)}
            className="CardList__button"
            onClick={() => {
              onClick(item);
              if (isLocationSelected(state.location)) {
                dispatch({
                  type: FormReducer.update,
                  field: FormFields.location,
                  payload: locationInitialState,
                });
              }
            }}
          >
            {renderCard(item)}
          </button>
        ))}

        {totalCount > MAX_ITEMS && items.length !== totalCount && (
          <button
            className="CardList__more"
            onClick={() => {
              setSectionLoading(sectionKey, true);
              setShowAll(true);
              performSearch(
                searchValue,
                sectionKey === "doctors" ? true : showAllDoctors,
                sectionKey === "services" ? true : showAllServices,
                sectionKey === "specializations"
                  ? true
                  : showAllSpecializations,
              ).finally(() => setSectionLoading(sectionKey, false));
            }}
            disabled={loadingSections[sectionKey]}
          >
            {loadingSections[sectionKey] ? (
              <LoadingAnimation
                text={bookingSearchSetup?.loadingResultsText || "Laster"}
              />
            ) : sectionKey === "specializations" &&
              bookingSearchSetup?.showAllSpecializationsButtonLabel ? (
              bookingSearchSetup.showAllSpecializationsButtonLabel.replaceAll(
                "$count",
                totalCount.toString(),
              )
            ) : sectionKey === "services" &&
              bookingSearchSetup?.showAllServicesButtonLabel ? (
              bookingSearchSetup.showAllServicesButtonLabel.replaceAll(
                "$count",
                totalCount.toString(),
              )
            ) : sectionKey === "doctors" &&
              bookingSearchSetup?.showAllDoctorsButtonLabel ? (
              bookingSearchSetup.showAllDoctorsButtonLabel.replaceAll(
                "$count",
                totalCount.toString(),
              )
            ) : (
              `Vis alle ${totalCount} ${title.toLowerCase()}`
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {searchValue.trim().length >= 2 ? (
        <>
          {(isTyping || isLoading) &&
            !showAllServices &&
            !showAllDoctors &&
            !showAllSpecializations && (
              <BookingSearchLoading
                message={bookingSearchSetup.loadingResultsText}
              />
            )}

          {error && (
            <div className="InitialStep__searchFallback">
              <p className="error">{error}</p>
            </div>
          )}

          {!isLoading && !isTyping && !error && totalItemsLength === 0 && (
            <div className="InitialStep__searchFallback">
              <p>{bookingSearchSetup.noResultsMessage || "Ingen treff"}</p>
            </div>
          )}

          {!error && totalItemsLength > 0 && (
            <div className="InitialStep__searchResults">
              {renderSection({
                title: bookingSearchSetup.servicesResultsHeading || "Tjeneste",
                sectionKey: "services",
                items: displayServices,
                totalCount: serviceTotal,
                setShowAll: setShowAllServices,
                getKey: (service) => `service-${service.id}`,
                onClick: (service) => {
                  const fullService = rootData.services.find(
                    (s) => String(s.containerId) === String(service.id),
                  );
                  if (!fullService) {
                    console.error("Service not found in rootData");
                    return;
                  }
                  selectContainer(
                    fullService,
                    rootData.treatments,
                    dispatch,
                    state,
                    setStepByName,
                  );
                },
                renderCard: (service) => (
                  <Card
                    className="CardList__card"
                    heading={service.name}
                    label={service.specializationName}
                    showArrow
                  />
                ),
              })}

              {renderSection({
                title:
                  bookingSearchSetup.specializationsResultsHeading ||
                  "Spesialitet",
                sectionKey: "specializations",
                items: displaySpecializations,
                totalCount: specializationTotal,
                setShowAll: setShowAllSpecializations,
                getKey: (spec) => `specialization-${spec.id}`,
                onClick: (spec) => {
                  const container = findTreatmentById(
                    rootData.treatments,
                    spec.id.toString(),
                  );
                  selectContainer(
                    container,
                    rootData.treatments,
                    dispatch,
                    state,
                    setStepByName,
                  );
                  setSearchValue("");
                },
                renderCard: (spec) => (
                  <Card
                    className="CardList__card"
                    heading={spec.name}
                    showArrow
                  />
                ),
              })}

              {renderSection({
                title: bookingSearchSetup.doctorsResultsHeading || "Behandler",
                sectionKey: "doctors",
                items: displayDoctors,
                totalCount: doctorTotal,
                setShowAll: setShowAllDoctors,
                getKey: (doctor) => `doctor-${doctor.id}`,
                onClick: (doctor) => {
                  setResults({} as BookingSearchResponse);
                  setSearchValue("");
                  if (doctor.id) {
                    const foundDoctor = rootData.doctors.find(
                      (d) => d.id === doctor.id.toString(),
                    );
                    if (foundDoctor)
                      selectDoctorAndLocation(
                        foundDoctor,
                        rootData.centers,
                        dispatch,
                      );
                  }
                },
                renderCard: (doctor) => (
                  <Card
                    className="CardList__card"
                    heading={doctor.name}
                    imageUrl={doctor.pictureUrl}
                    label={doctor.jobTitle}
                    showArrow
                  />
                ),
              })}
            </div>
          )}
        </>
      ) : searchValue.trim().length > 0 ? (
        <div className="InitialStep__searchFallback">
          <p>
            {bookingSearchSetup.tooShortSearchInputMessage
              ? bookingSearchSetup.tooShortSearchInputMessage
              : "Skriv inn minst 2 tegn"}
          </p>
        </div>
      ) : null}

      {searchValue && bookingSearchSetup.defaultTreatment && (
        <div className="InitialStep__notFoundHelp">
          <h3>
            {bookingSearchSetup.defaultTreatmentHeading ||
              "Finner du ikke det du leter etter?"}
          </h3>
          <button
            onClick={() => {
              if (bookingSearchSetup.defaultTreatment) {
                const container = findTreatmentById(
                  rootData.treatments,
                  bookingSearchSetup.defaultTreatment,
                );
                selectContainer(
                  container,
                  rootData.treatments,
                  dispatch,
                  state,
                  setStepByName,
                );
                setSearchValue("");
              }
            }}
          >
            {bookingSearchSetup.defaultTreatmentButtonLabel ||
              "Bestille en allmenntime"}
          </button>
        </div>
      )}
    </>
  );
};
